import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiRequest } from '@/features/videos-common/api/client';
import {
  buildCreateVideoBody,
  buildHomeOrderBody,
  buildUpdateVideoBody,
  buildUrlBody
} from '@/features/staff-settings-videos/api/requestBodies';
import { DEFAULT_PAGE_SIZE, pagePath, pageResults } from '@/features/staff-settings-videos/api/pagination';
import { applyHomeSet } from '@/features/staff-settings-videos/utils/homeSet';
import { STAFF_PLAYLISTS_KEY, STAFF_VIDEOS_KEY } from './queryKeys';

/**
 * A video as returned by the server. Field names match the steward-api
 * Swagger StaffVideo schema. `durationSeconds`, `thumbnailUrl`,
 * `readTimeMinutes` and `slots` are read-only: the first two change only
 * through POST /staff/videos/:id/refresh, slots only through
 * PUT /staff/videos/home-order.
 *
 * @typedef {Object} StaffVideo
 * @property {number} id
 * @property {string} youtubeVideoId
 * @property {string} title
 * @property {string|null} description
 * @property {number|null} durationSeconds
 * @property {string|null} thumbnailUrl
 * @property {string|null} transcript
 * @property {number|null} readTimeMinutes
 * @property {string|null} resourceTitle
 * @property {string|null} resourceDescription
 * @property {string|null} resourceUrl
 * @property {boolean} isPublished
 * @property {import('@/features/videos-common/audiences').IntentTag[]} audiences - Intent tags; empty means it can be pinned nowhere
 * @property {import('@/features/videos-common/audiences').HomeVideoSlot[]} slots
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * Response of GET /staff/videos?page=&pageSize=. pageSize is 0 to 500
 * (default 100); pageSize 0 returns only `count`.
 *
 * @typedef {Object} StaffVideosPage
 * @property {number} count - Total videos across all pages
 * @property {number} page - 1-based
 * @property {number} pageSize
 * @property {boolean} next - Whether a later page has videos
 * @property {boolean} previous - Whether page is greater than 1
 * @property {StaffVideo[]} results
 */

/**
 * Response of POST /staff/videos/prefill.
 *
 * @typedef {Object} StaffVideoPrefill
 * @property {string} youtubeVideoId
 * @property {string} title
 * @property {number|null} durationSeconds
 * @property {string|null} descriptionSuggestion
 * @property {string|null} thumbnailUrl - informational only, never sent back
 */

/**
 * GET /staff/videos: one page of videos, including unpublished, newest first.
 * The cache holds the whole paginated response ({ count, page, pageSize,
 * next, previous, results }); `data` is just the page's videos.
 *
 * @param {{ page?: number, pageSize?: number }} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<StaffVideo[]>}
 */
export function useStaffVideos({ page = 1, pageSize = DEFAULT_PAGE_SIZE } = {}) {
  const request = useApiRequest();

  return useQuery({
    queryKey: [...STAFF_VIDEOS_KEY, { page, pageSize }],
    queryFn: () => request(pagePath('/staff/videos', { page, pageSize })),
    select: pageResults,
    staleTime: 0
  });
}

/** Refetch everything a video change can affect. */
function useInvalidateVideos() {
  const queryClient = useQueryClient();
  return () => Promise.all([
    queryClient.invalidateQueries(STAFF_VIDEOS_KEY),
    queryClient.invalidateQueries(STAFF_PLAYLISTS_KEY)
  ]);
}

/**
 * POST /staff/videos/prefill. Writes nothing on the server.
 * mutate(url) resolves to a StaffVideoPrefill.
 */
export function usePrefillVideo() {
  const request = useApiRequest();

  return useMutation({
    mutationFn: (url) => request('/staff/videos/prefill', {
      method: 'POST',
      body: buildUrlBody(url)
    })
  });
}

/** POST /staff/videos. mutate({ youtubeVideoId, values }). */
export function useCreateVideo() {
  const request = useApiRequest();
  const invalidate = useInvalidateVideos();

  return useMutation({
    mutationFn: ({ youtubeVideoId, values }) => request('/staff/videos', {
      method: 'POST',
      body: buildCreateVideoBody(youtubeVideoId, values)
    }),
    onSuccess: invalidate
  });
}

/** PATCH /staff/videos/:id. mutate({ id, values }). */
export function useUpdateVideo() {
  const request = useApiRequest();
  const invalidate = useInvalidateVideos();

  return useMutation({
    mutationFn: ({ id, values }) => request(`/staff/videos/${id}`, {
      method: 'PATCH',
      body: buildUpdateVideoBody(values)
    }),
    onSuccess: invalidate
  });
}

/** DELETE /staff/videos/:id. mutate(id). */
export function useDeleteVideo() {
  const request = useApiRequest();
  const invalidate = useInvalidateVideos();

  return useMutation({
    mutationFn: (id) => request(`/staff/videos/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate
  });
}

/**
 * POST /staff/videos/:id/refresh. Only durationSeconds and thumbnailUrl
 * change on the server. mutate(id) resolves to the refreshed StaffVideo.
 */
export function useRefreshVideo() {
  const request = useApiRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => request(`/staff/videos/${id}/refresh`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries(STAFF_VIDEOS_KEY)
  });
}

/**
 * PUT /staff/videos/home-order with one audience's full ordered list of ids.
 * mutate({ audience, videoIds }). Other audiences' sets are untouched.
 * On success the cache is updated from the server's response; success or
 * failure, the list is then refetched so the UI always shows server state
 * (on failure that reverts to the saved order).
 */
export function useHomeOrder() {
  const request = useApiRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ audience, videoIds }) => request('/staff/videos/home-order', {
      method: 'PUT',
      body: buildHomeOrderBody(audience, videoIds)
    }),
    onSuccess: (homeSet, { audience }) => {
      // Every cached page of the list; pages are keyed under STAFF_VIDEOS_KEY.
      queryClient.setQueriesData(STAFF_VIDEOS_KEY, (videosPage) => (
        videosPage && Array.isArray(videosPage.results)
          ? { ...videosPage, results: applyHomeSet(videosPage.results, audience, homeSet) }
          : videosPage
      ));
    },
    onSettled: () => queryClient.invalidateQueries(STAFF_VIDEOS_KEY)
  });
}
